import React, {useCallback} from 'react';

import {Button, ButtonAppearance, ButtonSize, SplitButton} from '@kit';

import {useDice} from '../../../../components/dice';
import type {FormulaResult} from '../../../../components/dice';
import {cls, humanEnumeration} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import type {RollContext, RollMode} from '../model/conditions';
import {
	D20,
	DiceFormula,
	formatDice,
	formatRollResult,
	isD20Test,
	parseDice,
	withBonus,
	withRollMode,
} from '../model/dice';
import {withJournalEntry} from '../model/journal';

import css from './sheet.module.css';

export type RollRequest = {
	title: string;
	formula: DiceFormula;
	mode?: RollMode;
	/** что за бросок d20: к нему добавляются кости Благословения и подобных эффектов */
	context?: RollContext;
	/** итог броска, когда кости остановились */
	onTotal?(total: number): void;
	/** весь бросок: выпавшие значения каждой кости и итог */
	onResult?(result: FormulaResult): void;
};

const MODE_TITLES: Record<RollMode, string> = {
	normal: '',
	advantage: 'с преимуществом',
	disadvantage: 'с помехой',
};

export function useRoll() {
	const {roll} = useDice();
	const {character, update} = useCharacter();
	const penalty = character.d20Penalty;

	return useCallback(
		({title, formula, mode = 'normal', context, onTotal, onResult}: RollRequest) => {
			const withPenalty = isD20Test(formula) && penalty ? withBonus(formula, -penalty) : formula;
			const bonuses = context ? character.rollBonuses(context) : [];
			const rolled = withRollMode(withPenalty, mode);
			const withBonusDice = {
				...rolled,
				dice: [...rolled.dice, ...bonuses.flatMap((bonus) => parseDice(bonus.dice).dice)],
			};
			const rollTitle = [
				title,
				mode !== 'normal' && MODE_TITLES[mode],
				bonuses.length > 0 && bonuses.map((bonus) => bonus.title).join(', '),
			]
				.filter(Boolean)
				.join(', ');
			roll(formatDice(withBonusDice), rollTitle, (result) => {
				update((current) =>
					withJournalEntry(current, {
						kind: 'roll',
						title: rollTitle,
						details: formatRollResult(result),
						total: result.total,
					}),
				);
				onTotal?.(result.total);
				onResult?.(result);
			});
		},
		[roll, update, penalty, character],
	);
}

type RollButtonProps = {
	title: string;
	/** бонус к броску d20 */
	bonus?: number;
	/** своя формула вместо d20 с бонусом: урон и другие броски без преимущества */
	formula?: DiceFormula;
	/** что за бросок d20: от этого зависят преимущество и помеха от состояний */
	context?: RollContext;
	className?: string;
	/** класс обёртки кнопки со стрелкой: рамка вокруг кнопки и стрелки */
	groupClassName?: string;
	appearance?: ButtonAppearance;
	size?: ButtonSize;
	/** кнопка со стрелкой растягивается на всю ширину строки */
	block?: boolean;
	/** вызывается при броске: так бросок заклинания тратит ячейку или использование */
	onRoll?(): void;
	children: React.ReactNode;
};

export const RollButton: React.FC<RollButtonProps> = ({
	title,
	bonus = 0,
	formula,
	context,
	className,
	groupClassName,
	appearance = 'subtle',
	size,
	block,
	onRoll,
	children,
}) => {
	const roll = useRoll();

	if (formula && !isD20Test(formula)) {
		return (
			<Button
				className={className}
				appearance={appearance}
				size={size}
				title={`Бросить: ${title}`}
				onClick={() => {
					onRoll?.();
					roll({title, formula});
				}}
			>
				{children}
			</Button>
		);
	}

	return (
		<D20RollMenu
			title={title}
			formula={formula || withBonus(D20, bonus)}
			context={context}
			className={className}
			groupClassName={groupClassName}
			appearance={appearance}
			size={size}
			block={block}
			onRoll={onRoll}
		>
			{children}
		</D20RollMenu>
	);
};

type D20RollMenuProps = Omit<RollButtonProps, 'bonus' | 'formula'> & {formula: DiceFormula};

const D20RollMenu: React.FC<D20RollMenuProps> = ({
	title,
	formula,
	context,
	className,
	groupClassName,
	appearance,
	size,
	block,
	onRoll,
	children,
}) => {
	const {character} = useCharacter();
	const roll = useRoll();
	const advice = context ? character.rollAdvice(context) : undefined;
	const suggested = advice?.mode || 'normal';
	const reasons = humanEnumeration(advice?.reasons || []);

	const hint = [
		advice?.autoFail.length && `автоматический провал: ${humanEnumeration(advice.autoFail)}`,
		reasons &&
			`${
				suggested === 'advantage' ? 'преимущество' : suggested === 'disadvantage' ? 'помеха' : 'эффекты'
			}: ${reasons}`,
	]
		.filter(Boolean)
		.join('; ');

	const rollWith = (mode: RollMode) => {
		onRoll?.();
		roll({title, formula, mode, context});
	};

	return (
		<SplitButton
			appearance={appearance}
			size={size}
			block={block}
			className={groupClassName}
			buttonClassName={cls(
				suggested === 'advantage' && css.rollAdvantage,
				suggested === 'disadvantage' && css.rollDisadvantage,
				className,
			)}
			title={`Бросить: ${title}${hint ? ` (${hint})` : ''}`}
			onClick={() => rollWith(suggested)}
			items={ROLL_MODES}
			value={suggested}
			onSelect={(mode) => rollWith(mode as RollMode)}
			menuLabel={`Режим броска: ${title}`}
			menuHeader={
				<>
					<span className={css.rollMenuTitle}>{title}</span>
					{hint && <span className={css.rollMenuWarning}>{hint}</span>}
				</>
			}
		>
			{children}
		</SplitButton>
	);
};

export const ROLL_MODES: {value: RollMode; label: string}[] = [
	{value: 'normal', label: 'Обычный бросок'},
	{value: 'advantage', label: 'С преимуществом'},
	{value: 'disadvantage', label: 'С помехой'},
];
